import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import { withSearchHandler } from 'decorators';

import { Button } from 'components/core/Button';
import { Link } from 'components/core/Link';
import { Group } from 'components/core/Page';
import { Paragraph, Text } from 'components/core/Typography';
import { SearchBox } from 'components/search/SearchBox';

import { Search } from 'components/svg';
import pinIcon from 'assets/images/icons/informed-search/pin.svg';
import { locateUser } from 'utils/GeoLocation';

import './locationSearchBox.scss';

const withLocationSearchBoxLogic = (WrappedComponent) => (props) => {
	const [locating, setLocating] = useState(false);
	const [error, setError] = useState(false);

	const locateMe = useCallback(() => {
		setLocating(true);
		setError(false);

		locateUser({
			onSuccess: ({ latitude, longitude }) => {
				props.handleSearch({
					locationParams: { latitude, longitude },
				});
				setLocating(false);
			},
			onError: () => {
				setError(true);
				setLocating(false);
			},
		});
	}, [props.handleSearch]);

	return (
		<WrappedComponent
			{...props}
			locating={locating}
			setLocating={setLocating}
			error={error}
			setError={setError}
			locateMe={locateMe}
		/>
	);
};

export const enhance = (Component) =>
	withSearchHandler(withLocationSearchBoxLogic(Component));

export function LocationSearchBox({
	handleSearch,
	locateMe,
	locating,
	error,
	searchLink,
	searchLocation,
	inputLabel,
}) {
	return (
		<div className="location-search-box">
			{error && (
				<Group className="search-error">
					<Paragraph
						size="small"
						color="red"
					>
						We're having trouble finding your location.
					</Paragraph>
					{/* If there's a searchLocation and link we can send them to, show that */}
					{searchLocation?.city && searchLink ? (
						<Paragraph last>
							Please enter your ZIP code or{' '}
							<Link
								href={searchLink}
								rel="nofollow"
							>
								see all options in {searchLocation.city}
							</Link>
						</Paragraph>
					) : (
						<Paragraph last>
							Please enter your ZIP code instead.
						</Paragraph>
					)}
				</Group>
			)}
			<Group>
				<div className="location-search">
					<div className="search-top">
						<Button
							onClick={locateMe}
							disabled={locating}
							loading={locating}
							className="search-near-me"
						>
							<span className="icon-wrapper">
								<img
									src={pinIcon}
									alt=""
									aria-hidden="true"
									className="search-icon"
								/>{' '}
								Storage near me
							</span>
						</Button>
						<div className="or-text">
							<Text size="small">OR</Text>
						</div>
					</div>
					<div className="search-bottom">
						<SearchBox
							inputLabel={inputLabel}
							handleSearch={handleSearch}
							buttonIcon={true}
							buttonText="Find Storage"
							id="storage-deals-hero-input"
						/>
					</div>
				</div>
			</Group>
		</div>
	);
}

LocationSearchBox.propTypes = {
	handleSearch: PropTypes.func,
	locateMe: PropTypes.func,
	locating: PropTypes.bool,
	searchLink: PropTypes.string,
	searchLocation: PropTypes.shape({
		city: PropTypes.string,
	}),
	error: PropTypes.bool,
	inputLabel: PropTypes.string.isRequired,
};

export default enhance(LocationSearchBox);
